// Edit a single drip step's CONTENT (message/media/poll) in flight, reusing the
// wizard's exact per-step editor (StepEditor → DripMessageEditor). It hydrates
// the shared DripEditorReducer working copy from the live drip, renders the step
// editor for one index, and on Save writes the whole steps array back through the
// dbService thunk (optimistic). Reset on close so the wizard re-hydrates fresh.
import React, { useEffect } from 'react';
import { namespace } from '@common/config/constants';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBDripThunks } from '@common-reducers/DBServiceThunks';
import { hydrate, reset } from '@common-reducers/DripEditorReducer';
import type { Drip } from '@bt-common/schemas/bt/drip';
import { StepEditor } from './drip-message-editor';
import { CampaignEditorDialog } from './campaign-editor-dialog';
import { buildSteps, hydrateSteps, hydrateTiming } from './wizard';

interface DripStepEditModalProps {
  drip: Drip;
  stepIndex: number;
  onClose: () => void;
}

export const DripStepEditModal: React.FC<DripStepEditModalProps> = ({ drip, stepIndex, onClose }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const sliceSteps = useAppSelector((s) => s.DripEditorReducer.steps);
  const ready = useAppSelector(
    (s) => s.DripEditorReducer.hydrated && s.DripEditorReducer.dripId === drip._id,
  );

  // Hydrate the shared working copy with THIS drip on open; reset on close so a
  // later wizard open re-hydrates from scratch (its guard keys on hydrated+dripId).
  useEffect(() => {
    dispatch(hydrate({
      dripId: drip._id ?? '',
      steps: hydrateSteps(drip),
      name: drip.name ?? '',
      // `?? false` mirrors the backend, which exits only on stopOnReply === true.
      stopOnReply: drip.stopOnReply ?? false,
      timing: hydrateTiming(drip),
    }));
    return () => { dispatch(reset()); };
    // hydrate once per opened drip; step switching within the same drip reuses it
  }, [dispatch, drip._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = stepIndex === 0
    ? t('campaignsRedesign.wizard.messageLabel', 'Message 1')
    : t('campaignsRedesign.wizard.followupLabel', 'Follow-up {{number}}', { number: stepIndex });

  const save = () => {
    dispatch(DBDripThunks.patch({ entity: { ...drip, steps: buildSteps(sliceSteps) } }));
    onClose();
  };

  const title = t('campaignsRedesign.stepEditModal.title', 'Edit {{label}}', { label });

  // The schedule-message editor's layout: the editor card, and the WhatsApp
  // preview as its own panel beside it (CampaignEditorDialog). Templates and
  // Save as template are the editor toolbar's own buttons.
  return (
    <CampaignEditorDialog onClose={onClose} title={title} showPreview={ready}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--line)' }}>
        <strong style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</strong>
        <button type="button" className="btn ghost icon sm" onClick={onClose} aria-label={t('common.close', 'Close')}><X size={16} /></button>
      </div>
      {/* Padded: StepEditor is the wizard's editor, which relies on its host
          for gutters; bare, it ran edge to edge. */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '20px' }}>
        {ready ? <StepEditor idx={stepIndex} showPreview={false} /> : <div style={{ padding: 24, color: 'var(--muted)', fontSize: 13 }}>{t('campaignsRedesign.editModal.loadingEditor', 'Loading editor…')}</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--line)' }}>
        <button type="button" className="btn ghost" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
        <button type="button" className="btn primary" onClick={save}>{t('common.saveChanges', 'Save changes')}</button>
      </div>
    </CampaignEditorDialog>
  );
};
